import React, { act, useEffect, useState } from "react";
import { BASE_URL } from "@/dashboardQueries";
import { AccreditationGroup } from "@/models/accreditation";
import { PaginatedResponse } from "@/models/Dashboard";
import axios from "axios";
import {
  BanknoteIcon,
  BoxesIcon,
  CheckCircle,
  ChevronDownIcon,
  Clock,
  Shapes,
  TrashIcon,
  UserIcon,
} from "lucide-react";
import { useQuery } from "react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAccreditationStore } from "@/store/accreditation";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  DragOverEvent,
  useSensors,
  MouseSensor,
  useSensor,
} from "@dnd-kit/core";
import { SortableContext, useSortable, arrayMove } from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { PencilSquareIcon } from "@heroicons/react/20/solid";
import { GroupOptionsButton } from "./GroupOptionsDropdown";
import { Pagination } from "../Pagination";
import { Search } from "../Search";
import { TicketIssueStatus } from "@/constants/accreditation";
import { Menu } from "@headlessui/react";
import { BulkAction, BulkActionsButton } from "../BulkActionsButton";

interface Props<T> {
  name: string;
  onRowClick: (groupId: number) => void;
  handleDelete: (group: T) => void;
  handleEditOpen: (groupId: number) => void;
  data: PaginatedResponse<T> | undefined;
  renderRowContent: (item: T) => React.ReactNode;
  groupHeading: React.ReactNode;
  onOrderUpdate: (
    model: string,
    payload: Array<{ itemId: number; order: number }>
  ) => void;
  onSearch?: (search: string) => void;
  hideOptionsButton?: boolean;
  page: number;
  onPageChange?: (page: number) => void;
  bulkActions: Array<BulkAction>;
}

export const GroupList = <T extends { id: number }>({
  name,
  onRowClick,
  handleDelete,
  handleEditOpen,
  data,
  renderRowContent,
  groupHeading,
  onOrderUpdate,
  onSearch,
  hideOptionsButton,
  page,
  onPageChange,
  bulkActions,
}: Props<T>) => {
  const { setRequestPaymentIndividual, bulkActionsLoading } =
    useAccreditationStore();
  const navigate = useNavigate();
  const [groupBeingDragged, setGroupBeingDragged] =
    useState<AccreditationGroup | null>(null);
  const [activeId, setActiveId] = useState<string | number | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [items, setItems] = useState<T[]>(data ? data.results : []);

  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [lastCheckedId, setLastCheckedId] = useState<number | null>(null);

  // Clear selectedIds when bulkActionsLoading transitions from true to false
  useEffect(() => {
    if (!bulkActionsLoading) {
      setSelectedIds(new Set());
    }
  }, [bulkActionsLoading]);

  // If we went from bulkActionsLoading to not loading, clear the selectedIds

  useEffect(() => {
    setItems(data ? data.results : []);
  }, [data]);

  const handlePageChange = (page: number) => {
    // setSearchParams(();
    const queryParamName = `${name.toLowerCase()}Page`;
    searchParams.set(queryParamName, page.toString());
    setSearchParams(searchParams);
    onPageChange && onPageChange(page);
  };

  const handleCheckboxChange = (id: number, isChecked: boolean) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (isChecked) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  };

  const handleSelectAll = (isChecked: boolean) => {
    if (isChecked) {
      setSelectedIds(new Set(items.map((item) => item.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleShiftClick = (id: number) => {
    if (lastCheckedId !== null) {
      const startIndex = items.findIndex((item) => item.id === lastCheckedId);
      const endIndex = items.findIndex((item) => item.id === id);
      const range = items.slice(
        Math.min(startIndex, endIndex),
        Math.max(startIndex, endIndex) + 1
      );
      setSelectedIds((prev) => {
        const newSet = new Set(prev);
        range.forEach((item) => newSet.add(item.id));
        return newSet;
      });
    }
    setLastCheckedId(id);
  };

  const isRowSelected = (id: number) => selectedIds.has(id);

  const isAllSelected = items.length > 0 && selectedIds.size === items.length;

  // Customizing the MouseSensor to disable dragging on buttons
  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      delay: 100, // Add delay to prevent accidental drags
      tolerance: 5, // Slight movement tolerance before starting a drag
    },
    onActivation: ({ event }) => {
      // Prevent drag activation if the target is a button
      if (
        event.target instanceof HTMLElement &&
        event.target.closest("button, input")
      ) {
        return false;
      }
      return true;
    },
  });

  const sensors = useSensors(mouseSensor);

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setActiveId(active.id);
    const selectedGroup = items.find(
      (group: AccreditationGroup) => group.id === active.id
    );
    if (selectedGroup) {
      setGroupBeingDragged(selectedGroup);
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event;
    setOverId(over?.id ?? null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex(
        (group: AccreditationGroup) => group.id === active.id
      );
      const newIndex = items.findIndex(
        (group: AccreditationGroup) => group.id === over.id
      );

      const newItems = arrayMove(items, oldIndex, newIndex);
      await onOrderUpdate(
        name.toLowerCase(),
        newItems.map((item, index) => {
          return { itemId: item.id, order: index + 1 };
        })
      );
      setItems(newItems);
    }
    setActiveId(null);
    setOverId(null);
    setGroupBeingDragged(null);
  };

  interface SortableGroupRowProps<T> {
    group: T;
    onRowClick: (id: number) => void;
    handleDelete: (item: T) => void;
    handleEditOpen: (id: number) => void;
    renderRowContent: (item: T) => React.ReactNode;
    hideOptionsButton?: boolean;
    selectedIds: Set<number>;
  }

  const SortableGroupRow = <T extends { id: number }>({
    group,
    onRowClick,
    handleDelete,
    handleEditOpen,
    renderRowContent,
    hideOptionsButton,
    selectedIds,
  }: SortableGroupRowProps<T>) => {
    const { listeners, attributes, setNodeRef } = useSortable({ id: group.id });

    const isOver = overId === group.id;
    const isSelected = isRowSelected(group.id);

    const individualHasPaymentLink = group?.tickets?.some(
      (ticket) => ticket.order
    );

    return (
      <div
        ref={setNodeRef}
        {...listeners}
        {...attributes}
        className={`flex justify-between items-center p-3 border-b border-gray-200 dark:border-white/10 relative ${
          false ? "px-4" : "px-6"
        }`}
        onClick={() => onRowClick(group.id)}
      >
        {isOver && (
          <div className="absolute inset-x-0 top-0 h-1 bg-blue-500"></div>
        )}
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => handleCheckboxChange(group.id, e.target.checked)}
            onClick={(e) => {
              // Handles both regular and shift-click
              e.stopPropagation();

              if (e.shiftKey) {
                handleShiftClick(group.id);
              } else {
                setLastCheckedId(group.id);
              }
            }}
            className="mr-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600 dark:bg-dark-secondary dark:border-white/10 dark:focus:ring-indigo-600"
          />

          {renderRowContent(group)}
        </div>
        <div className="flex items-center space-x-2">
          {individualHasPaymentLink && group?.tickets[0]?.order && (
            <>
              {group?.tickets[0].status === TicketIssueStatus.PENDING ? (
                <span
                  id="payment-status-badge"
                  className="inline-flex items-center rounded-md bg-yellow-400/10 px-2 py-1 text-xs font-medium text-yellow-500 ring-1 ring-inset ring-yellow-400/20"
                >
                  Payment Pending
                  <Clock className="ml-1.5 h-3.5 w-3.5 text-yellow-500" />
                </span>
              ) : (
                <span
                  id="payment-status-badge"
                  className="inline-flex items-center rounded-md bg-green-400/10 px-2 py-1 text-xs font-medium text-green-500 ring-1 ring-inset ring-green-400/20"
                >
                  Paid
                  <CheckCircle className="ml-1.5 h-3.5 w-3.5 text-green-500" />
                </span>
              )}
            </>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation(); // Prevent row click when clicking the button
              handleDelete(group);
            }}
            type="button"
            id="delete"
            className="inline-flex items-center rounded-md bg-red-400/10 px-2 py-1 text-xs font-medium text-red-400 ring-1 ring-inset ring-red-400/20"
          >
            <TrashIcon className="text-red-500 h-4 w-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation(); // Prevent row click when clicking the button
              handleEditOpen(group.id);
            }}
            type="button"
            className="inline-flex items-center rounded-md bg-green-400/10 px-2 py-1 text-xs font-medium text-green-400 ring-1 ring-inset ring-green-400/20 ml-2"
          >
            <PencilSquareIcon className="text-green-500 h-4 w-4" />
          </button>
        </div>
      </div>
    );
  };

  return items.length > 0 ? (
    <div className="w-full">
      <DndContext
        sensors={sensors}
        modifiers={[restrictToVerticalAxis]}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={items}>
          <div className="bg-white dark:bg-dark-secondary shadow">
            <div
              className={`border-b dark:border-white/10 px-5 ${
                false ? "px-5" : "px-6"
              } flex items-center ${selectedIds.size > 0 ? "py-1" : "py-3"}`}
            >
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className="mr-4 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600 dark:bg-dark-secondary dark:border-white/10 dark:focus:ring-indigo-600"
                />

                {selectedIds && selectedIds.size > 0 && (
                  <span className="ml-2 text-sm text-gray-500 flex">
                    {selectedIds.size} selected
                  </span>
                )}
              </div>
              {/* todo: make actions configurable based on which list we are selecting */}
              {selectedIds.size > 0 && (
                <div className="flex-1 justify-end flex -mr-3">
                  <BulkActionsButton
                    actions={bulkActions}
                    selectedIds={selectedIds}
                    type={name === "Individuals" ? "individual" : "group"}
                  />
                </div>
              )}
            </div>
            {items.map((group) => (
              <SortableGroupRow
                key={group.id}
                group={group}
                onRowClick={onRowClick}
                handleDelete={handleDelete}
                handleEditOpen={handleEditOpen}
                renderRowContent={renderRowContent}
                hideOptionsButton={hideOptionsButton}
                selectedIds={selectedIds}
              />
            ))}
          </div>
        </SortableContext>

        {groupBeingDragged && (
          <DragOverlay>
            <div className="dark:bg-dark-secondary bg-zinc-50 border-y dark:border-white/10 pl-6 py-3 flex items-center">
              <input
                type="checkbox"
                checked={false}
                // onChange={(e) => handleSelectAll(e.target.checked)}
                className="mr-4 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600 dark:bg-dark-secondary dark:border-white/10 dark:focus:ring-indigo-600"
              />
              {renderRowContent(items.filter((item) => item.id == activeId)[0])}
            </div>
          </DragOverlay>
        )}
      </DndContext>
      <>
        {name !== "Individuals" &&
          data &&
          data.count !== data.results.length && (
            <Pagination
              currentPage={page}
              totalPages={Math.ceil(data.count / data.perPage)}
              onPageChange={(page: number) => handlePageChange(page)}
            />
          )}
      </>
    </div>
  ) : (
    <></>
  );
};
